import {
  isLiveSubscriptionStatus,
  LIVE_SUBSCRIPTION_STATUSES,
} from "@intelligems/ig-types";

import type { BillingSnapshot, BillingSnapshotInput } from "./types";

const LIVE_STATUS_PRECEDENCE = new Map<string, number>(
  LIVE_SUBSCRIPTION_STATUSES.map((status, index) => [status, index])
);

export const monthsPerPeriod = (
  interval: string,
  intervalCount: number | null | undefined
): number => {
  const count = intervalCount ?? 1;
  return interval === "year" ? count * 12 : count;
};

export const normalizeFlatPriceToMonthly = (
  flatPrice: number,
  interval: string,
  intervalCount: number | null | undefined
): number => Math.round(flatPrice / monthsPerPeriod(interval, intervalCount));

export const buildBillingSnapshot = ({
  subscriptionAccessData,
  livePrices,
  stripeSubscriptionSummaries = [],
}: BillingSnapshotInput): BillingSnapshot => {
  const actualPriceByProductId = new Map<string, number>();
  const subscriptionLinks = stripeSubscriptionSummaries.flatMap(
    ({ billingProvider, id, stripeSubscriptionId }) =>
      id && stripeSubscriptionId && billingProvider
        ? [{ billingProvider, id, stripeSubscriptionId }]
        : []
  );

  // Every status Stripe still bills, `past_due` included — the ownership rule
  // `formatModules` applies, for the same reason (see catalog.ts).
  //
  // Ordered, and first writer wins, because this map is what makes a negotiated
  // flat price beat the catalog's list rate and an org can carry more than one
  // live subscription. Taking the payload's own order let a stale row overwrite
  // the one that actually bills the merchant — and `normalizeFlatPriceToMonthly`
  // divides by *that* row's interval, so an annual leftover quoted a twelfth of
  // its price against a monthly subscription. Precedence is the same everywhere
  // this feature must pick one live subscription: status rank, then id.
  //
  // A lower-precedence sibling still supplies a product the primary does not
  // carry; modules genuinely live on sibling subscriptions.
  const byBillingPrecedence = stripeSubscriptionSummaries
    .filter(({ status }) => isLiveSubscriptionStatus(status))
    .toSorted(
      (a, b) =>
        (LIVE_STATUS_PRECEDENCE.get(a.status ?? "") ??
          Number.MAX_SAFE_INTEGER) -
          (LIVE_STATUS_PRECEDENCE.get(b.status ?? "") ??
            Number.MAX_SAFE_INTEGER) ||
        (a.stripeSubscriptionId ?? "").localeCompare(
          b.stripeSubscriptionId ?? ""
        )
    );

  for (const subscription of byBillingPrecedence) {
    for (const link of subscription.stripeProductLinks) {
      const flatPrice = link.stripeProduct?.flatPrice;
      if (typeof flatPrice !== "number" || flatPrice <= 0) {
        continue;
      }

      if (actualPriceByProductId.has(link.stripeProductId)) {
        continue;
      }

      actualPriceByProductId.set(
        link.stripeProductId,
        normalizeFlatPriceToMonthly(
          flatPrice,
          subscription.interval,
          subscription.intervalCount
        )
      );
    }
  }

  return {
    subscriptionAccessData,
    livePrices,
    actualPriceByProductId,
    subscriptionLinks,
  };
};
